import React, { useEffect, useRef, useState } from "react";
import {
  Camera,
  Plus,
  Clock,
  KeyRound,
  LogOut,
  PanelRight,
  Edit3,
  Check,
  X,
} from "lucide-react";
import "../styles/components/profile.css";
import { useAuth } from "../context/AuthContext";
import { AuthService } from "../services/auth";
import { PostsService } from "../services/postsService";
import { ProfilesService, ProfileResponseDto } from "../services/profilesService";
import { useNavigate } from "react-router-dom";
import ChangePasswordModal from "./ChangePasswordModal";
import { toAbsoluteUrl } from "../config/api";
import { showToast } from '../utils/toastUtils';

interface ProfileProps {
  onClose: () => void;
}

const Profile: React.FC<ProfileProps> = ({ onClose }) => {
  const { user: authUser, logout } = useAuth();
  const [profile, setProfile] = useState<ProfileResponseDto | null>(null);
  const [tokenBalance, setTokenBalance] = useState<number | 'unlimited'>(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isEditingFullName, setIsEditingFullName] = useState(false);
  const [editingFullName, setEditingFullName] = useState('');

  const getInitials = (nameOrEmail?: string) => {
    const src = (nameOrEmail || '').trim();
    if (!src) return 'U';
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    const single = parts[0];
    // Nếu là email, hãy lấy chữ cái đầu tiên trước '@'
    const base = single.includes('@') ? single.split('@')[0] : single;
    return (base[0] || 'U').toUpperCase();
  };

  useEffect(() => {
    const load = async () => {
      try {
        // Người dùng hiện tại bao gồm tokenBalance và role từ /auth/me
        const me = await AuthService.getCurrentUser();
        const role = (me as any).role;
        if (role === 'admin' || role === 'moderator' || role === 'tool_user') {
          setTokenBalance('unlimited');
        } else {
          // Lấy stats để kích hoạt refresh token miễn phí mỗi ngày & đồng bộ tokenBalance
          try {
            const stats = await PostsService.getUserPostStats();
            const refreshedBalance = (stats?.user?.tokenBalance ?? (me as any).tokenBalance) || 0;
            setTokenBalance(refreshedBalance);
          } catch {
            setTokenBalance((me as any).tokenBalance || 0);
          }
        }
      } catch {}
      try {
        const p = await ProfilesService.getMyProfile();
        const withAbs = p && p.avatar && !/^https?:\/\//.test(p.avatar)
          ? { ...p, avatar: toAbsoluteUrl(p.avatar) }
          : p;
        setProfile(withAbs);
      } catch {}
    };
    load();
  }, []);

  const handlePickAvatar = () => fileInputRef.current?.click();
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    
    try {
      const updated = await ProfilesService.uploadAvatar(profile.id, file);
      const withAbs = updated && updated.avatar && !/^https?:\/\//.test(updated.avatar)
        ? { ...updated, avatar: toAbsoluteUrl(updated.avatar) }
        : updated;
      setProfile(withAbs);
      
      // Reset file input để có thể chọn cùng file lần nữa
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Lỗi khi upload avatar:', error);
      showToast.error('Có lỗi xảy ra khi upload ảnh đại diện. Vui lòng thử lại.');
    }
  };

  const handleChangePassword = async () => {
    setShowChangePassword(true);
  };

  const handleLogout = () => {
    logout();
    onClose();
    navigate("/");
  };

  const gotoHistory = () => {
    navigate("/member/historyuser");
    onClose();
  };

  const handleEditFullName = () => {
    setEditingFullName(profile?.fullName || authUser?.fullName || '');
    setIsEditingFullName(true);
  };

  const handleSaveFullName = async () => {
    if (!profile) return;
    
    try {
      const updated = await ProfilesService.upsertMyProfile({
        fullName: editingFullName.trim()
      });
      
      // Xử lý avatar để đảm bảo đường dẫn tuyệt đối và giữ nguyên avatar hiện tại nếu API không trả về
      const withAbs = updated && updated.avatar && !/^https?:\/\//.test(updated.avatar)
        ? { ...updated, avatar: toAbsoluteUrl(updated.avatar) }
        : updated;
      
      // Nếu API không trả về avatar, giữ nguyên avatar hiện tại
      const finalProfile = withAbs?.avatar ? withAbs : { ...withAbs, avatar: profile.avatar };
      
      setProfile(finalProfile);
      setIsEditingFullName(false);
    } catch (error) {
      console.error('Lỗi khi cập nhật họ tên:', error);
      showToast.error('Có lỗi xảy ra khi cập nhật họ tên. Vui lòng thử lại.');
    }
  };

  const handleCancelEditFullName = () => {
    setIsEditingFullName(false);
    setEditingFullName('');
  };

  return (
    <div className="profile-panel">
      {/* Nút đóng sidepanel */}
      <button className="side-close" onClick={onClose}>
        <PanelRight size={22} />
      </button>

      {/* Avatar + coin */}
      <div className="profile-header">
        <div className="avatar-wrapper">
          {profile?.avatar ? (
            <img src={profile.avatar} alt="Avatar" className="profile-avatar" />
          ) : (
            <div
              className="profile-avatar"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#e0e7ff',
                color: '#3730a3',
                fontWeight: 600,
                fontSize: 18,
                userSelect: 'none'
              }}
              title={authUser?.fullName || profile?.fullName || authUser?.email || profile?.email || ''}
            >
              {getInitials(authUser?.fullName || profile?.fullName || authUser?.email || profile?.email)}
            </div>
          )}
          <button className="avatar-camera" onClick={handlePickAvatar}>
            <Camera size={18} color="#1976d2" />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
        </div>

        <div className="profile-coins">
          <span className="coin">🪙 {tokenBalance === 'unlimited' ? 'Vô hạn' : tokenBalance}</span>
          <button className="btn-plus" onClick={() => navigate('/member/service')}>
            <Plus size={16} />
          </button>
        </div>
      </div>

      {/* Thông tin tài khoản */}
      <div className="profile-box">
        <h3 className="section-title">Thông tin tài khoản</h3>

        <div className="profile-field">
          <label>Họ và tên</label>
          {isEditingFullName ? (
            <div className="edit-field">
              <input
                type="text"
                value={editingFullName}
                onChange={(e) => setEditingFullName(e.target.value)}
                className="edit-input"
                placeholder="Nhập họ và tên"
                autoFocus
              />
              <div className="edit-actions">
                <button 
                  className="btn-save" 
                  onClick={handleSaveFullName}
                  title="Lưu"
                >
                  <Check size={16} />
                </button>
                <button 
                  className="btn-cancel" 
                  onClick={handleCancelEditFullName}
                  title="Hủy"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ) : (
            <div className="field-value">
              <span className="fullname">
                {profile?.fullName || authUser?.fullName || 'Chưa cập nhật'}
              </span>
              <button 
                className="btn-edit" 
                onClick={handleEditFullName}
                title="Chỉnh sửa họ tên"
              >
                <Edit3 size={16} />
              </button>
            </div>
          )}
        </div>

        <div className="profile-field">
          <label>Email</label>
          <span className="email">{authUser?.email || profile?.email || ''}</span>
        </div>
      </div>

      {/* Action buttons */}
      <div className="profile-box">
        <div className="action-row">
          <button className="btn-outline" onClick={gotoHistory}>
            Lịch Sử Giao Dịch <Clock size={16} />
          </button>
          <button className="btn-outline" onClick={handleChangePassword}>
            Đổi Mật Khẩu <KeyRound size={16} />
          </button>
        </div>
        <button className="btn-primary btn-custom" onClick={handleLogout}>
          <LogOut size={18} /> Đăng Xuất
        </button>
        {showChangePassword && (
          <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
        )}
      </div>
    </div>
  );
};

export default Profile;
